/**
 * F41 (Wave 2 Track D — admin) — tournament moderation list page.
 *
 * Route: `/tournaments`
 * Permission: `tournaments:read` (super_admin + support + content_moderator).
 *
 * Chip-row status filter (`?status=`) + a sport <Select> filter (`?sport=`) + a
 * "Suspended only" Chip toggle (`?suspended=true`) + a free-text SearchInput
 * (client-side title/organizer match). Each row links to the detail page where
 * the suspend/edit/cancel/force-refund actions live. Post-hoc moderation (D13)
 * — there is no pre-approval queue. Mirrors F40's promotions + F32's open-games
 * moderation list shape.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Chip,
  EmptyState,
  Pagination,
  SearchInput,
  Select,
  Skeleton,
  Stat,
  StatusChip,
  TournamentStatusBadge,
  type TournamentStatusBadgeStatus,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { SPORT_NAME_VALUES, TOURNAMENT_STATUSES, type TournamentStatus } from '@bravemove/types';
import {
  adminTournamentsApi,
  type AdminTournamentRow,
  type ListTournamentsOpts,
} from '../../api/tournaments';
import { CreateTournamentDialog } from '../../components/tournaments/CreateTournamentDialog';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { usePageKpis } from '../../hooks/usePageKpis';

const PAGE_SIZE = 25;

type UiStatusFilter = TournamentStatus | 'all';
const DEFAULT_STATUS: UiStatusFilter = 'all';
const STATUS_TABS: ReadonlyArray<UiStatusFilter> = ['all', ...TOURNAMENT_STATUSES];

/**
 * Map the canonical F41 `TournamentStatus` (which carries `registration_open` /
 * `registration_closed`) onto the `TournamentStatusBadge` variants. The primitive
 * has no `registration_closed` variant, so that one falls back to a `StatusChip`.
 * `registration_open` reuses the primitive's `published` ("Reg open") treatment.
 */
function asTournamentBadgeStatus(status: TournamentStatus): TournamentStatusBadgeStatus | null {
  switch (status) {
    case 'draft':
      return 'draft';
    case 'published':
    case 'registration_open':
      return 'published';
    case 'live':
      return 'live';
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    default:
      return null; // 'registration_closed' — no badge variant
  }
}

function humaniseSport(sport: string): string {
  return sport.replace(/-/g, ' ');
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

export function AdminTournamentsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // D19 — admin-create on behalf of an organizer (super_admin + content_moderator).
  const canCreate = auth.adminUser?.permissions?.includes('tournaments:create') ?? false;
  const [createOpen, setCreateOpen] = useState(false);

  const statusParam = (searchParams.get('status') as UiStatusFilter | null) ?? DEFAULT_STATUS;
  const sportParam = searchParams.get('sport') ?? '';
  const suspendedParam = searchParams.get('suspended') === 'true';
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;

  const [rows, setRows] = useState<readonly AdminTournamentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // F61 (A3) — one unaudited `GET /admin/page-kpis` (was 2–4 audited
  // `perPage=1` list reads); refreshed after any admin action.
  const kpiCounts = usePageKpis('tournaments');

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opts: ListTournamentsOpts = {
        status: statusParam,
        page: pageParam,
        perPage: PAGE_SIZE,
        // rev-17 — sport is a SERVER-side filter (`?sport=` → tournaments.sport_name).
        // The previous client-side post-fetch filter silently hid matching rows on
        // other server pages and collapsed the total to the current page's count.
        ...(sportParam ? { sport: sportParam } : {}),
        ...(suspendedParam ? { suspended: true } : {}),
      };
      const data = await adminTournamentsApi.listTournaments(opts);
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [statusParam, sportParam, suspendedParam, pageParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const setParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      next.delete('page');
      return next;
    });
  };

  const onStatusChange = (value: UiStatusFilter) =>
    setParam('status', value === DEFAULT_STATUS ? null : value);

  const onPageChange = (next: number) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('page', String(next));
      return np;
    });
  };

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.title.toLowerCase().includes(q) || r.organizerB2bAccountId.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const sportOptions = useMemo(
    () => [
      { value: '', label: t('admin:tournament.list.filterSport', { defaultValue: 'Sport' }) },
      ...SPORT_NAME_VALUES.map((s) => ({ value: s, label: humaniseSport(s) })),
    ],
    [t],
  );

  const columns = useMemo<readonly TableColumn<AdminTournamentRow>[]>(
    () => [
      {
        key: 'title',
        header: t('admin:tournament.list.colTitle', { defaultValue: 'Title' }),
        cell: (row) => (
          <span className="text-[13px] text-ink line-clamp-1 max-w-[28ch]">{row.title}</span>
        ),
      },
      {
        key: 'sport',
        header: t('admin:tournament.list.colSport', { defaultValue: 'Sport' }),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2 capitalize">
            {humaniseSport(row.sportName)}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('admin:tournament.list.colStatus', { defaultValue: 'Status' }),
        cell: (row) => {
          const badgeStatus = asTournamentBadgeStatus(row.status);
          return (
            <div className="flex items-center gap-1.5">
              {badgeStatus ? (
                <TournamentStatusBadge
                  size="sm"
                  status={badgeStatus}
                  label={t(`tournament:status.${row.status}`)}
                />
              ) : (
                <StatusChip
                  size="sm"
                  status="submitted"
                  label={t(`tournament:status.${row.status}`)}
                />
              )}
              {row.suspendedAt ? (
                <TournamentStatusBadge
                  size="sm"
                  status="suspended_by_admin"
                  label={t('admin:tournament.list.suspendedBadge', { defaultValue: 'Suspended' })}
                  data-testid={`tournament-suspended-badge-${row.id}`}
                />
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'organizer',
        header: t('admin:tournament.list.colOrganizer', { defaultValue: 'Organizer' }),
        cell: (row) => (
          <span className="text-[11px] font-mono text-ink-3">
            {row.organizerB2bAccountId.slice(0, 8)}
          </span>
        ),
      },
      {
        key: 'dates',
        header: t('admin:tournament.list.colDates', { defaultValue: 'Dates' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2 tabular-nums">
            {formatDate(row.startDate, locale)}–{formatDate(row.endDate, locale)}
          </span>
        ),
      },
      {
        key: 'registered',
        header: t('admin:tournament.list.colRegistered', { defaultValue: 'Registered' }),
        align: 'end',
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2 font-mono tabular-nums">
            {row.totalRegisteredCount}
            {row.totalCapacity !== null ? `/${row.totalCapacity}` : ''}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="tournaments-list-page">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title={t('admin:tournament.list.title', { defaultValue: 'Tournaments' })}
          subtitle={t('admin:tournament.list.subtitle', {
            defaultValue:
              'Cross-org tournament listings. Filter by status or sport; suspend abusive listings.',
          })}
        />
        {canCreate ? (
          <Button
            variant="primary"
            onClick={() => setCreateOpen(true)}
            data-testid="tournament-create-cta"
          >
            {t('admin:tournament.list.createCta', { defaultValue: 'Create tournament' })}
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="tournaments-kpi-strip">
        <Stat
          label={t('admin:tournament.kpi.published', { defaultValue: 'Published' })}
          value={kpiCounts === null ? '—' : kpiCounts.published}
        />
        <Stat
          label={t('admin:tournament.kpi.registrationOpen', { defaultValue: 'Registration open' })}
          value={kpiCounts === null ? '—' : kpiCounts.registrationOpen}
        />
        <Stat
          label={t('admin:tournament.kpi.live', { defaultValue: 'Live' })}
          value={kpiCounts === null ? '—' : kpiCounts.live}
        />
        <Stat
          label={t('admin:tournament.kpi.suspended', { defaultValue: 'Suspended' })}
          value={kpiCounts === null ? '—' : kpiCounts.suspended}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div
          role="tablist"
          aria-label={t('admin:tournament.list.filterStatus', { defaultValue: 'Status' })}
          className="flex flex-wrap items-center gap-2"
        >
          {STATUS_TABS.map((tab) => (
            <Chip
              key={tab}
              selected={statusParam === tab}
              onClick={() => onStatusChange(tab)}
              data-testid={`tournament-status-chip-${tab}`}
            >
              {tab === 'all'
                ? t('admin:tournament.list.all', { defaultValue: 'All' })
                : t(`tournament:status.${tab}`)}
            </Chip>
          ))}
        </div>

        <Chip
          selected={suspendedParam}
          onClick={() => setParam('suspended', suspendedParam ? null : 'true')}
          data-testid="tournament-suspended-toggle"
        >
          {t('admin:tournament.list.filterSuspended', { defaultValue: 'Suspended only' })}
        </Chip>

        <div className="w-44">
          <Select
            size="sm"
            options={sportOptions}
            value={sportParam}
            onChange={(e) => setParam('sport', e.target.value || null)}
            aria-label={t('admin:tournament.list.filterSport', { defaultValue: 'Sport' })}
            data-testid="tournament-sport-select"
          />
        </div>

        <div className="w-56">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin:tournament.list.searchPlaceholder', {
              defaultValue: 'Title or organizer id',
            })}
            data-testid="tournament-search"
          />
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="tournaments-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : visibleRows.length === 0 ? (
        <EmptyState
          title={t('admin:tournament.list.empty', { defaultValue: 'No tournaments in this view.' })}
        />
      ) : (
        <>
          <Table
            data={visibleRows as AdminTournamentRow[]}
            columns={columns as TableColumn<AdminTournamentRow>[]}
            rowKey={(row) => row.id}
            onRowPress={(row) => navigate(`/tournaments/${row.id}`)}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination page={pageParam} pageCount={pageCount} onChange={onPageChange} />
            </div>
          ) : null}
        </>
      )}

      {/* D19 — admin create-tournament (rev-11 follow-up). */}
      <CreateTournamentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          setCreateOpen(false);
          navigate(`/tournaments/${created.id}`);
        }}
      />
    </div>
  );
}

export default AdminTournamentsPage;
